/**
 * Market intelligence resolver — Supabase edition.
 *
 * One bulk read per request, indexed by `ci_id` in memory, cached an hour.
 * The matcher scores all 5,877 careers on every submit; per-career fetches
 * would be 5,877 round trips.
 *
 * Precedence (unchanged):  live → seed → bundled fallback → neutral(50)
 *
 * Rules that carry over:
 *  - `source = 'seed'` rows never expire. The 24h staleness rule applies to
 *    non-seed rows only. Applied to the seed it would expire the whole table
 *    the day after import and drop every career to neutral.
 *  - A stale live row is used and flagged, not discarded. Last month's salary
 *    beats no salary.
 *  - A miss scores 50 and flags the career. Never zero.
 */
import { supabase } from "@/integrations/supabase/client";
import type { DemandBand, MarketData } from "@/data/careersBundle";

export const MARKET_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
export const STALE_AFTER_MS = 24 * 60 * 60 * 1000; // non-seed rows only

export type MarketRowSource = "seed" | "firecrawl" | "manual";

export interface MarketRow {
  ci_id: string;
  salary_lo_lpa: number | null;
  salary_hi_lpa: number | null;
  market_demand_2040: string | null;
  automation_exposure: number | null;
  source: MarketRowSource;
  updated_at: string;
  observations?: number | null;
}

export interface MarketIndex {
  /** Row count actually read. 0 means the table is empty or unreachable. */
  size: number;
  fetchedAt: number;
  get(ciId: string): MarketData | null;
}

const EMPTY_INDEX: MarketIndex = { size: 0, fetchedAt: 0, get: () => null };

let cache: { at: number; index: MarketIndex } | null = null;
let inflight: Promise<MarketIndex> | null = null;

function toMarketData(row: MarketRow, now: number): MarketData {
  const isSeed = row.source === "seed";
  const ageMs = now - new Date(row.updated_at).getTime();
  // Seed rows never expire. Non-seed rows older than 24h are used and flagged.
  const stale = !isSeed && Number.isFinite(ageMs) && ageMs > STALE_AFTER_MS;

  return {
    salaryLoLpa: row.salary_lo_lpa,
    salaryHiLpa: row.salary_hi_lpa,
    demand2040: (row.market_demand_2040 as DemandBand | null) ?? null,
    automationExposure: row.automation_exposure,
    source: isSeed ? "seed" : "live",
    stale,
    observations: row.observations ?? null,
  };
}

function buildIndex(rows: MarketRow[], now: number): MarketIndex {
  const map = new Map<string, MarketData>();
  for (const r of rows) {
    if (!r?.ci_id) continue;
    map.set(r.ci_id, toMarketData(r, now));
  }
  return { size: map.size, fetchedAt: now, get: (id) => map.get(id) ?? null };
}

/**
 * Bulk-read the whole market table once and index it. Cached for an hour.
 * Never throws: on failure we return an empty index, and the caller falls
 * through to the bundled fallback, then neutral.
 */
export async function loadMarketIntelligence(
  opts: { force?: boolean } = {},
): Promise<MarketIndex> {
  const now = Date.now();
  if (!opts.force && cache && now - cache.at < MARKET_CACHE_TTL_MS) return cache.index;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const PAGE = 1000;
      const COLS =
        "ci_id,salary_lo_lpa,salary_hi_lpa,market_demand_2040,automation_exposure,source,updated_at,observations";
      const page = (from: number, withCount = false) =>
        supabase
          .from("career_market_intelligence")
          .select(COLS, withCount ? { count: "exact" } : undefined)
          .order("ci_id", { ascending: true })
          .range(from, from + PAGE - 1);
      // First page tells us the total; the rest load side by side instead of
      // one after another (each round trip is slow on mobile networks).
      const first = await page(0, true);
      if (first.error) throw first.error;
      const rows: MarketRow[] = [...((first.data ?? []) as unknown as MarketRow[])];
      const total = first.count ?? rows.length;
      const rest = [];
      for (let from = PAGE; from < total; from += PAGE) rest.push(page(from));
      for (const r of await Promise.all(rest)) {
        if (r.error) throw r.error;
        rows.push(...((r.data ?? []) as unknown as MarketRow[]));
      }
      const index = buildIndex(rows, Date.now());
      cache = { at: Date.now(), index };
      return index;
    } catch (err) {
      console.warn("[market] bulk read failed, using bundled fallback:", err);
      return EMPTY_INDEX;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Test / dev escape hatch. */
export function clearMarketCache() {
  cache = null;
}
